import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import fleetApi from "@/lib/fleetApi";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/portal/widgets";
import { fmtTime, fmtDayKey, todayKey } from "@/lib/time";
import { Printer, ArrowLeft } from "lucide-react";

export default function RunSheetPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { organization } = useAuth();
  const date = sp.get("date") || todayKey();
  const only = sp.get("run");
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/runs?date=${date}`), fleetApi.get(`/trips?date=${date}&limit=5000`)])
      .then(([r, t]) => {
        const byId = Object.fromEntries((t.data || []).map((x) => [x.id, x]));
        const runs = (r.data || []).filter((x) => !only || x.id === only)
          .map((x) => ({ ...x, stops: x.stops.filter((s) => x.trip_ids.includes(s.trip_id) && byId[s.trip_id]).map((s) => ({ ...s, trip: byId[s.trip_id] })) }))
          .filter((x) => x.stops.length).sort((a, b) => (a.stops[0].eta || "").localeCompare(b.stops[0].eta || ""));
        setData(runs);
      }).catch(() => setData([]));
  }, [date, only]);

  if (!data) return <Loading />;
  return (
    <div data-testid="run-sheets-page">
      <div className="print:hidden">
        <PageHeader title="Run sheets" subtitle={`${data.length} group run${data.length === 1 ? "" : "s"} on ${fmtDayKey(date)} · one page per van`}>
          <button onClick={() => navigate(`/portal/dispatch?date=${date}`)} data-testid="run-sheets-back"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 hover:bg-slate-800"><ArrowLeft className="h-3.5 w-3.5" /> Back to trips</button>
          <button onClick={() => window.print()} disabled={!data.length} data-testid="run-sheets-print"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-40"><Printer className="h-4 w-4" /> Print {data.length > 1 ? "all sheets" : "sheet"}</button>
        </PageHeader>
        {!data.length && <p className="rounded-md border border-slate-800 bg-slate-900/60 p-6 text-sm text-slate-300" data-testid="run-sheets-empty">No group runs assigned on this day yet. Build them from Trips &amp; Dispatch → Group runs.</p>}
      </div>
      <div id="run-sheets" className="space-y-8">
        {data.map((r) => <Sheet key={r.id} r={r} date={date} org={organization?.name} />)}
      </div>
    </div>
  );
}

function Sheet({ r, date, org }) {
  const to = r.direction === "to";
  const kids = r.stops.length;
  const escorts = r.stops.filter((s) => s.trip.escort).length;
  const wc = r.stops.some((s) => s.trip.wheelchair);
  return (
    <article className="run-sheet mx-auto max-w-3xl rounded-lg bg-white p-8 text-slate-900 shadow-xl" data-testid={`run-sheet-${r.id}`}>
      <header className="flex items-start justify-between border-b-2 border-slate-900 pb-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{org || "Company"} · Run sheet</p>
          <h2 className="mt-1 text-2xl font-bold">{to ? "To" : "From"} {r.center}</h2>
          <p className="text-sm text-slate-600">{fmtDayKey(date)} · {to ? "Morning drop-off" : "Pick-up from center"}</p>
        </div>
        <dl className="text-right text-sm">
          <div><dt className="inline text-slate-500">Driver </dt><dd className="inline font-bold" data-testid={`run-sheet-driver-${r.id}`}>{r.driver_name}</dd></div>
          <div><dt className="inline text-slate-500">Van </dt><dd className="inline font-bold">{r.vehicle ? `#${String(r.vehicle).replace(/^#/, "")}` : "—"}</dd></div>
          <div className="text-slate-600">{kids} rider{kids === 1 ? "" : "s"}{escorts ? ` · ${escorts} escort${escorts === 1 ? "" : "s"}` : ""}{wc ? " · wheelchair" : ""}</div>
        </dl>
      </header>
      <table className="mt-4 w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-400 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Est. time</th><th className="py-2 pr-2">Rider</th>
            <th className="py-2 pr-2">{to ? "Pick up at" : "Drop off at"}</th><th className="py-2 pr-2">Notes</th><th className="w-16 py-2">Done ✓</th>
          </tr>
        </thead>
        <tbody>
          {r.stops.map((s, i) => <StopRow key={s.trip_id} s={s} i={i} to={to} rid={r.id} />)}
          <tr className="border-t-2 border-slate-900 font-semibold">
            <td className="py-3 pr-2">★</td><td className="py-3 pr-2">—</td><td className="py-3 pr-2" colSpan={2}>{to ? `Arrive ${r.center}` : `Start at ${r.center}`}</td><td colSpan={2} />
          </tr>
        </tbody>
      </table>
      <footer className="mt-6 grid grid-cols-2 gap-6 text-xs text-slate-500">
        <p>Times are estimates — follow the driver app for live updates. Never leave a child unattended; hand off only to staff or an authorized adult.</p>
        <p className="border-t border-slate-400 pt-6 text-center">Driver signature</p>
      </footer>
    </article>
  );
}

function StopRow({ s, i, to, rid }) {
  const t = s.trip;
  const notes = [t.escort && "Escort rides", t.wheelchair && "Wheelchair", t.special_instructions, t.mobility_notes].filter(Boolean).join(" · ");
  return (
    <tr className="border-b border-slate-200 align-top" data-testid={`run-sheet-stop-${rid}-${i}`}>
      <td className="py-2 pr-2 font-bold">{i + 1}</td>
      <td className="py-2 pr-2 font-mono">{s.eta ? fmtTime(s.eta) : "—"}<div className="text-[10px] text-slate-500">sched {fmtTime(t.scheduled_pickup)}</div></td>
      <td className="py-2 pr-2"><div className="font-semibold">{t.rider_name}</div><div className="text-[11px] text-slate-500">{t.external_trip_id}{t.rider_phone ? ` · ${t.rider_phone}` : ""}</div></td>
      <td className="py-2 pr-2">{to ? t.pickup_address : t.dropoff_address}</td>
      <td className="py-2 pr-2 text-xs">{notes || "—"}</td>
      <td className="py-2"><span className="inline-block h-5 w-5 border-2 border-slate-500" /></td>
    </tr>
  );
}
